import { Dropdown, Layout, MenuProps, message, Radio, Row, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useDispatch } from "react-redux";
import ArrowBottomGreenIcon from "../../../assets/icons/arrow-bottom-green.svg";
import ArrowRightGreenIcon from "../../../assets/icons/arrow-right-green.svg";
import GreenBottomChevronIcon from "../../../assets/icons/green-bottom-chevron.svg";
import InfoBlackGreyIcon from "../../../assets/icons/info-black-grey.svg";
import FilterBlackIcon from "../../../assets/icons/filter-black.svg";
import EurFlagImg from "../../../assets/images/eur-flag.webp";
import CreatorRevenueChartComponent from "../../../components/CreatorRevenueChartComponent";
import CreatorRevenueTabComponent from "../../../components/CreatorRevenueTabComponent";
import FooterComponent from "../../../components/FooterComponent";
import FormationModalComponent from "../../../components/Formations/Creators/Modals";
import InfoModal from "../../../components/InfoModal";
import SpinnerComponent from "../../../components/SpinnerComponent/SpinnerComponent";
import { FOOTER_IDS } from "../../../helpers/constant";
import { IRevenue } from "../../../helpers/types";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { AppDispatch } from "../../../redux/store";
import { updateTutorialVisibilityStatus } from "../../../redux/users/usersSlice";
import { userService } from "../../../services";
import { checkModalStatus } from "../../../utls/formationModalUtils";
import "./index.less";

const CreatorRevenuePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [revenue, setRevenue] = useState<IRevenue>();
  const [graphData, setGraphData] = useState<string>("today");
  const [isInfoModal, setIsInfoModal] = useState<boolean>(false);
  const [infoContent, setInfoContent] = useState<any>(null);
  const [isPendingPaymentOpen, setIsPendingPaymentOpen] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"daily" | "monthly" | "detailed">("monthly");

  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [offset, setOffset] = useState<number>(0);
  const [filter, setFilter] = useState<string>("");
  const [monthlyOffset, setMonthlyOffset] = useState<number>(0);
  const [monthlyHasMore, setMonthlyHasMore] = useState<boolean>(true);
  const [dailyOffset, setDailyOffset] = useState<number>(0);
  const [dailyHasMore, setDailyHasMore] = useState<boolean>(true);

  const [formationModalIsVisible, setFormationModalIsVisible] = useState<boolean>(false);
  const { currentUserSettings } = useFetchUserSettings();

  useEffect(() => {
    // TODO code to refactor
    checkModalStatus("hasSeenEarningModal", setFormationModalIsVisible, currentUserSettings);
  }, [currentUserSettings]);

  const loadMoreData = async () => {
    if (loading) return;

    if (activeTab === "detailed" && !hasMore) return;
    if (activeTab === "monthly" && !monthlyHasMore) return;
    if (activeTab === "daily" && !dailyHasMore) return;

    setLoading(true);
    try {
      if (activeTab === "detailed") {
        const response = await userService.getRevenue(graphData, 10, offset, filter, undefined, false);

        if (response?.detailPaymentReport?.length > 0) {
          setRevenue((prev) => {
            if (!prev) return undefined;

            return {
              ...prev,
              detailPaymentReport: [...(prev.detailPaymentReport || []), ...response.detailPaymentReport],
            };
          });

          setOffset((prev) => prev + response.detailPaymentReport.length);
          setHasMore(response.detailPaymentReport.length >= 10);
        } else {
          setHasMore(false);
        }
      } else if (activeTab === "monthly") {
        const response = await userService.getRevenue(graphData, 10, monthlyOffset, "", "monthly", false);

        if (response?.monthlyPreviousReport?.length > 0) {
          setRevenue((prev) => {
            if (!prev) return undefined;

            return {
              ...prev,
              monthlyPreviousReport: [...(prev.monthlyPreviousReport || []), ...response.monthlyPreviousReport],
            };
          });

          setMonthlyOffset((prev) => prev + response.monthlyPreviousReport.length);
          setMonthlyHasMore(response.monthlyPreviousReport.length >= 10);
        } else {
          setMonthlyHasMore(false);
        }
      } else if (activeTab === "daily") {
        const response = await userService.getRevenue(graphData, 10, dailyOffset, "", "daily", false);

        if (response?.dailyPreviousReport?.length > 0) {
          setRevenue((prev) => {
            if (!prev) return undefined;

            return {
              ...prev,
              dailyPreviousReport: [...(prev.dailyPreviousReport || []), ...response.dailyPreviousReport],
            };
          });

          setDailyOffset((prev) => prev + response.dailyPreviousReport.length);
          setDailyHasMore(response.dailyPreviousReport.length >= 10);
        } else {
          setDailyHasMore(false);
        }
      }
    } catch (error) {
      message.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === "detailed") {
          setOffset(0);
          setHasMore(true);

          const response = await userService.getRevenue(graphData, 10, 0, filter, undefined, true);

          if (response) {
            setRevenue(response);
            setOffset(response.detailPaymentReport?.length || 0);
            setHasMore((response.detailPaymentReport?.length || 0) >= 10);
          }
        } else if (activeTab === "monthly") {
          setMonthlyOffset(0);
          setMonthlyHasMore(true);

          const response = await userService.getRevenue(graphData, 10, 0, "", "monthly", true);

          if (response) {
            setRevenue(response);
            setMonthlyOffset(response.monthlyPreviousReport?.length || 0);
            setMonthlyHasMore((response.monthlyPreviousReport?.length || 0) >= 10);
          }
        } else if (activeTab === "daily") {
          setDailyOffset(0);
          setDailyHasMore(true);

          const response = await userService.getRevenue(graphData, 10, 0, "", "daily", true);

          if (response) {
            setRevenue(response);
            setDailyOffset(response.dailyPreviousReport?.length || 0);
            setDailyHasMore((response.dailyPreviousReport?.length || 0) >= 10);
          }
        }
      } catch (error) {
        console.error(error);
        message.error("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [graphData, activeTab, filter]);

  const currentHasMore = activeTab === "detailed" ? hasMore : activeTab === "monthly" ? monthlyHasMore : dailyHasMore;

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: currentHasMore,
    onLoadMore: loadMoreData,
  });

  const menuItems: MenuProps["items"] = [
    {
      key: "daily",
      label: t("daily"),
    },
    {
      key: "monthly",
      label: t("monthly"),
    },
    {
      key: "detailed",
      label: t("detailed"),
    },
  ];

  const filterMenuItems: MenuProps["items"] = [
    {
      key: "",
      label: t("all"),
    },
    {
      key: "monthly_subscription",
      label: t("monthly-subscription"),
    },
    {
      key: "stream tip",
      label: t("stream tip"),
    },
    {
      key: "collection",
      label: t("collection"),
    },
    {
      key: "mediapush",
      label: t("mediapush"),
    },
    {
      key: "privatemedia",
      label: t("privatemedia"),
    },
    {
      key: "stream",
      label: t("stream"),
    },
    {
      key: "unlock link",
      label: t("unlock link"),
    },
  ];

  const handleMenuClick = (e: { key: string }) => {
    setActiveTab(e.key as "daily" | "monthly" | "detailed");
  };

  const handleTutorialRead = async () => {
    dispatch(updateTutorialVisibilityStatus("hasSeenEarningModal"));
  };

  return (
    <Layout className="gradient-background-black-blue">
      <Layout.Content className="content creatorRevenueContentWrapper background-transparent">
        <Row className="relative pl-20 header-title-container-60">
          <Typography.Text className="header-title-black-35">{t("revenues")}</Typography.Text>
        </Row>
        <Row className="payment-information" align="middle">
          <Row className="payment-information-first-container">
            <Row className="next-payment-container">
              <Row>
                <Typography.Text className="font-32-bold text-white-color mr-10">
                  {revenue &&
                    revenue.nextInstallment?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) + " €"}
                </Typography.Text>
                {!isPendingPaymentOpen ? (
                  <img
                    src={ArrowRightGreenIcon}
                    width={34}
                    alt="open"
                    onClick={() => {
                      setIsPendingPaymentOpen(true);
                    }}
                  />
                ) : (
                  <img
                    src={ArrowBottomGreenIcon}
                    width={34}
                    alt="open"
                    onClick={() => {
                      setIsPendingPaymentOpen(false);
                    }}
                  />
                )}
              </Row>
              <Row>
                <Typography.Text>
                  <span className="text-description-first">{t("validated-for-the-next-payment-1")}</span>{" "}
                  <span className="text-description-second">{t("validated-for-the-next-payment-2")}</span>
                </Typography.Text>
                <img
                  src={InfoBlackGreyIcon}
                  width={12}
                  alt="info"
                  onClick={() => {
                    setIsInfoModal(true);
                    setInfoContent(1);
                  }}
                />
              </Row>
            </Row>
            <div className="currency-img">
              <img src={EurFlagImg} width={45} alt="EUR" />
              <Typography.Text className="eur-text">EUR</Typography.Text>
            </div>
          </Row>
          {isPendingPaymentOpen && (
            <Row className="payment-information-seconde-container">
              <Row className="pending-payment-container">
                <Row>
                  <Typography.Text className="font-24-bold text-white-color mr-10">
                    {revenue &&
                      revenue.waitingForValidation?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) + " €"}
                  </Typography.Text>
                </Row>
                <Row>
                  <Typography.Text>
                    <span className="text-description-first">{t("waiting-for-validation-1")}</span>{" "}
                    <span className="text-description-second">{t("waiting-for-validation-2")}</span>
                  </Typography.Text>
                  <img
                    src={InfoBlackGreyIcon}
                    width={12}
                    alt="info"
                    onClick={() => {
                      setIsInfoModal(true);
                      setInfoContent(2);
                    }}
                  />
                </Row>
              </Row>
            </Row>
          )}
        </Row>
        <Row className="revenue-radio-container">
          <Radio.Group
            defaultValue="today"
            onChange={(e) => {
              setGraphData(e.target.value);
            }}
            buttonStyle="solid"
          >
            <Space className="gap-0">
              <Radio.Button className="revenuePeriodRadio" value="today">
                {t("today")}
              </Radio.Button>
              <Radio.Button className="revenuePeriodRadio" value="week">
                {t("this-week")}
              </Radio.Button>
              <Radio.Button className="revenuePeriodRadio" value="month">
                {t("this-month")}
              </Radio.Button>
              <Radio.Button className="revenuePeriodRadio" value="total">
                {t("all")}
              </Radio.Button>
            </Space>
          </Radio.Group>
        </Row>
        <Row className="chart-container">
          <Row className="chart-data-row">
            <div>
              <Space className="gap-0" direction="vertical">
                <Typography.Text className="chart-price-text">
                  {(() => {
                    switch (graphData) {
                      case "today":
                        return (
                          revenue &&
                          revenue?.todayEarnings?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) + " €"
                        );
                      case "month":
                        return (
                          revenue &&
                          revenue?.oneMonthEarning?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) + " €"
                        );
                      case "week":
                        return (
                          revenue &&
                          revenue?.weeklyTotalEarnings?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) + " €"
                        );
                      case "total":
                        return (
                          revenue &&
                          revenue?.totalEarnings?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) + " €"
                        );
                      default:
                        return (
                          revenue &&
                          revenue?.todayEarnings?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) + " €"
                        );
                    }
                  })()}
                </Typography.Text>
                <Typography.Text className="chart-description-text">
                  {(() => {
                    switch (graphData) {
                      case "today":
                        return t("daily-total");
                      case "week":
                        return t("weekly-total");
                      case "month":
                        return t("monthly-total");
                      case "total":
                        return t("all-total");
                      default:
                        return t("daily-total");
                    }
                  })()}
                </Typography.Text>
              </Space>
            </div>
            <div>
              <Space className="gap-0" direction="vertical">
                <Typography.Text className="chart-price-text">
                  {revenue &&
                    revenue?.totalNetPrice?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) + " €"}
                </Typography.Text>
                <Typography.Text className="chart-description-text">{t("receipt-in-your-bank-account")}</Typography.Text>
              </Space>
            </div>
          </Row>
          <Row className="w-full">
            <CreatorRevenueChartComponent statsData={revenue?.statsData} graphData={graphData} />
          </Row>
        </Row>

        <Row className="last-report-container">
          <Row className="last-report-header">
            <Typography.Text className={`font-18-bold text-white-color ${activeTab === "detailed" ? "last-report-title" : ""}`}>
              {t("last-report")}
            </Typography.Text>
            <Row className="gap-10">
              {activeTab === "detailed" && (
                <Dropdown
                  menu={{
                    items: filterMenuItems,
                    onClick: (e) => {
                      setFilter(e.key);
                    },
                  }}
                  trigger={["click"]}
                  className={`revenue-dropdown ${filter ? "filter-button-active" : "filter-button"}`}
                >
                  <div className="filter-information-management-button">
                    {filter === "" && <Typography.Text className="filter-information-management-button-text">{t("filter")}</Typography.Text>}
                    {filter && <img src={FilterBlackIcon} width={12} alt="change" />}
                  </div>
                </Dropdown>
              )}
              <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={["click"]} className="revenue-dropdown">
                <div className="information-management-button">
                  <Typography.Text className="information-management-button-text">
                    {activeTab === "daily" && "Journalier"}
                    {activeTab === "monthly" && "Mensuel"}
                    {activeTab === "detailed" && "Détaillé"}
                  </Typography.Text>
                  <img src={GreenBottomChevronIcon} width={12} alt="change" />
                </div>
              </Dropdown>
            </Row>
          </Row>
          <Row className="last-report-content">
            {activeTab === "daily" && (
              <div className="detailed-report">
                <CreatorRevenueTabComponent dailyPreviousReport={revenue?.dailyPreviousReport} />
                {(loading || dailyHasMore) && <SpinnerComponent refProp={sentryRef} />}
              </div>
            )}

            {activeTab === "monthly" && (
              <div className="detailed-report">
                <CreatorRevenueTabComponent monthlyPreviousReport={revenue?.monthlyPreviousReport} />
                {(loading || monthlyHasMore) && <SpinnerComponent refProp={sentryRef} />}
              </div>
            )}

            {activeTab === "detailed" && (
              <div className="detailed-report">
                <CreatorRevenueTabComponent detailedPreviousReport={revenue?.detailPaymentReport} />
                {(loading || hasMore) && <SpinnerComponent refProp={sentryRef} />}
              </div>
            )}
          </Row>
        </Row>
      </Layout.Content>
      <FooterComponent page={FOOTER_IDS.REVENUES} type="creator" />
      <InfoModal
        isOpen={isInfoModal}
        onClose={() => {
          setIsInfoModal(false);
          setInfoContent(null);
        }}
        description={
          infoContent === 1 ? (
            t("earnings-confirm-for-payemnt")
          ) : infoContent === 2 ? (
            <span>
              {t("revenues-earning-validation-info-1")} <br />
              <br /> {t("revenues-earning-validation-info-2")}
            </span>
          ) : (
            ""
          )
        }
      />
      {formationModalIsVisible && <FormationModalComponent id="payment" visible={formationModalIsVisible} handleTutorialRead={handleTutorialRead} />}
    </Layout>
  );
};

export default CreatorRevenuePage;
